<template>
  <QaPageShell :title="$t('_QAAgents')" :loading="loading" :error="error" :empty="isEmpty" @retry="load">
    <template #filters>
      <QaFilterBar @change="load" />
    </template>

    <!-- ชนเพดานต่อคำขอ = อันดับและค่าเฉลี่ยคิดจากบางส่วน ห้ามตัดเงียบ -->
    <v-alert v-if="truncated" type="warning" variant="tonal" density="compact" class="mt-3" icon="mdi-alert-outline">
      <span class="text-body-2">ข้อมูลชนเพดานต่อคำขอ (20,000 เคส) — ตัวเลขของแต่ละคนคิดจากเท่าที่ดึงมาได้ในช่วง {{ windowDays }} วัน ไม่ใช่ทั้งหมด</span>
    </v-alert>

    <v-card class="qa-card mt-3" variant="flat">
      <v-card-title class="d-flex align-center py-3">
        <span class="text-subtitle-1">{{ $t('_QAAgents') }}</span>
        <v-spacer />
        <span class="text-caption text-medium-emphasis">{{ items.length }} คน</span>
      </v-card-title>
      <v-divider />
      <div class="qa-table-wrap pt-3">
        <v-data-table
          :headers="headers"
          :items="items"
          :items-per-page="15"
          class="elevation-0 my-data-table qa-table qa-table-mid"
          density="comfortable"
          fixed-header
          hover
          :row-props="{ class: 'qa-row-click' }"
          @click:row="openAgent"
        >
          <template #[`item.agentName`]="{ item }">
            <QaAgentCell :name="item.agentName" :initials="item.initials" :color="item.color" />
          </template>
          <template #[`item.avgScore`]="{ item }">
            <QaScoreCell :score="item.avgScore" />
          </template>
          <template #[`item.passRate`]="{ item }">
            <QaBar v-if="item.passRate != null" :percent="item.passRate" :value-text="item.passRate + '%'" />
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <!-- ⚠️ เคสที่ยังไม่ประเมินไม่ได้อยู่ในตัวเลขอื่นเลยสักช่อง (ทั้งค่าเฉลี่ย อัตราผ่าน
               และจำนวนเคส) · ถ้าไม่มีคอลัมน์นี้ คนจะอ่าน "เคสที่ประเมินแล้ว" เป็นภาระงานทั้งหมด
               ของเจ้าหน้าที่คนนั้น แล้วสรุปผลงานจากตัวส่วนที่ผิด -->
          <template #[`item.notEvaluated`]="{ item }">
            <span :class="item.notEvaluated ? 'text-medium-emphasis' : 'text-disabled'">{{ item.notEvaluated }}</span>
          </template>
          <template #[`item.failCount`]="{ item }">
            <span :class="item.failCount ? 'text-error font-weight-medium' : 'text-medium-emphasis'">{{ item.failCount }}</span>
          </template>
          <template #[`item.trend`]="{ item }">
            <!-- ⚠️ null = เทียบไม่ได้ (มีเคสประเมินสำเร็จน้อยกว่า 2 ใบ) — ลูกศรที่แปลว่า 0
                 กับที่แปลว่า "ยังไม่รู้" อ่านคนละเรื่องบนตารางประเมินผลคน -->
            <span v-if="item.trend == null" class="text-medium-emphasis">—</span>
            <span v-else :class="item.trend >= 0 ? 'text-success' : 'text-error'" class="text-body-2 font-weight-medium">
              <v-icon size="14">{{ item.trend >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
              {{ Math.abs(item.trend) }}
            </span>
          </template>
          <template #[`item.actions`]>
            <v-icon size="18" color="grey">mdi-chevron-right</v-icon>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </QaPageShell>
</template>

<script>
/**
 * ตารางผลงานเจ้าหน้าที่
 *
 * Q10 — ทุกแถวมาจาก `analytics/agents` ของจริง (เดิมสุ่มทั้งตารางจาก mock)
 *
 * ⚠️ คอลัมน์ "ทีม" หายไปแล้ว ไม่ใช่ย้ายที่ — ทีมของธนาคารไม่มีแหล่งข้อมูลในระบบนี้
 *    ที่มาแทนคือ "บทบาท" (Agent | Chatbot) ซึ่งเป็นค่าจริงจาก cc.QaAgents.Role
 *    (ข้อจำกัดข้อ 1 ของแผน Q10 — ถ้าวันหนึ่งมีทะเบียนทีมจริงค่อยเพิ่มคอลัมน์กลับ)
 */
import qaConsole from '@/services/qaConsole';
import QaPageShell from '@/components/QAConsole/QaPageShell.vue';
import QaFilterBar from '@/components/QAConsole/QaFilterBar.vue';
import QaAgentCell from '@/components/QAConsole/QaAgentCell.vue';
import QaScoreCell from '@/components/QAConsole/QaScoreCell.vue';
import QaBar from '@/components/QAConsole/QaBar.vue';

export default {
  name: 'QAAgents',
  components: { QaPageShell, QaFilterBar, QaAgentCell, QaScoreCell, QaBar },
  data() {
    return { loading: true, error: null, items: [], truncated: false, windowDays: 0 };
  },
  computed: {
    isEmpty() {
      return !this.loading && !this.error && !this.items.length;
    },
    headers() {
      return [
        { title: this.$t('qaAgent'), key: 'agentName', sortable: false, minWidth: '200px' },
        // 'บทบาท' ฮาร์ดโค้ดไทยแบบเดียวกับคอลัมน์อื่นที่มีเฉพาะใน QA Console (ยังไม่มีคีย์ i18n)
        { title: 'บทบาท', key: 'role', width: '120px' },
        // ⚠️ ชื่อคอลัมน์คือ "เคสที่ประเมินแล้ว" ไม่ใช่ "เคส" — caseCount นับเฉพาะเคสที่ประเมินสำเร็จ
        { title: this.$t('qaCasesEvaluated'), key: 'caseCount', align: 'end', width: '130px' },
        { title: this.$t('qaNotEvaluatedFilter'), key: 'notEvaluated', align: 'end', width: '110px' },
        { title: this.$t('qaAvgScore'), key: 'avgScore', align: 'end', width: '120px' },
        { title: this.$t('qaPassRate'), key: 'passRate', width: '160px' },
        { title: this.$t('qaFails'), key: 'failCount', align: 'end', width: '90px' },
        { title: this.$t('qaTrend'), key: 'trend', align: 'end', width: '100px' },
        { title: '', key: 'actions', sortable: false, align: 'end', width: '50px' },
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await qaConsole.getAgentScorecard(this.$store.getters['qaConsole/filter']);
        this.items = res.items || [];
        this.truncated = !!res.truncated;
        this.windowDays = res.windowDays;
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
    openAgent(_event, { item }) {
      this.$router.push({ name: 'QAAgentDetail', query: { agentId: item.agentId } });
    },
  },
};
</script>
